import React, { lazy } from 'react';
import { LinkDataType } from './internal-links';
import HomePageZoocasa from './themed-home-page-index/homepage-zoocasa';
import HomePageZoocasaLight from './themed-home-page-index/homepage-zoocasa-light';
import { RouteName } from 'components/dynamic-page/route-matchers';
import { HeadDataType } from 'components/head-data';
import {
  EXP_COMMERCIAL_CA_TENANT_ID,
  EXP_COMMERCIAL_TENANT_ID,
  EXP_IN_SERVICE_TENANT_ID,
  EXP_REALTY_CA_TENANT_ID,
  EXP_REALTY_US_TENANT_ID,
  ZOOCASA_TENANT_ID,
} from '@tenants';
import dynamic from 'next/dynamic';
import { PropsWithTenantId } from '@tenants/types';
import LoadingText from 'components/loading-text';
import { getTenantHomeConfig } from '@tenants/utils/configs';
import { getTenantById } from '@tenants';

import type { INamedContents } from 'pages';
import type { ListingCardData } from 'components/listing-card/listing-card';
import type { ExpListingCardDataWithCompliance } from 'types/listing-card';
import type { LastSearchUrlsResponse } from 'data/search-predictions';
import { LOCALE_HOME_PATHS } from 'types/locales';
import { ContentCard } from '@tenants/types';
import type { InsightCard } from './exp-components/insights-cards/InsightsCards';
import type { CountryCode } from 'types/countries';
import type { CityStats } from 'data/insights/types';
import type { AreaCardData } from './local-focus/area-cards';
import type { CityInsightsData } from './local-focus/city-insights';

/** Location resolved server-side (via request geo/IP) used to seed the local-focus homepage. */
export interface HomePageInsightsLocation {
  slug: string;
  name: string;
  latitude: number;
  longitude: number;
  country: CountryCode;
  province: string;
  city: string;
  hasAreaPage: boolean;
}

/**
 * Insights data fetched server-side for the local-focus homepage. Powers the
 * At a Glance, Area Cards, Market Dynamics, and City Insights (Deep Dive)
 * sections so they render with real data on first paint instead of fetching
 * client-side. `location` is null when no location could be resolved server-side.
 */
export interface HomePageInsights {
  location: HomePageInsightsLocation | null;
  cityStats: CityStats | null;
  popularAreaCards: AreaCardData[];
  /** City insights already resolved to the request locale; AI prose is stripped when the locale's translation is missing (only the Overview card hides). */
  cityInsights: CityInsightsData | null;
  /**
   * The insights locale the client should trigger generation for, or null when
   * the request locale is already satisfied. Set when no row exists yet, or when
   * a row exists but is missing the current locale's translation.
   */
  insightsGenerateLocale: string | null;
}

export type HomePageProps = PropsWithTenantId<{
  isCrawler: boolean;
  featuredHomesMlsNums: INamedContents['featuredHomesMlsNums'];
  isNotificationBannerHidden: boolean;
  availableHomeListings: ListingCardData[];
  availableHomeListingsV2?: ExpListingCardDataWithCompliance[];
  useListingCardWithCompliance?: boolean;
  availableHomeListingsMeta: number;
  availableHomeListingsLocation: string;
  internalLinksData: LinkDataType[];
  isExpandedArea?: boolean;
  maintenance?: Maintenance;
  routeName?: RouteName;
  headerData?: HeadDataType;
  soldHomeListings?: ListingCardData[];
  featuredHomeListings?: ListingCardData[];
  homePageUrls?: LastSearchUrlsResponse;
  contentCardsWithDynamicLink?: ContentCard[];
  insightsCards?: InsightCard[];
  enableLocalFocusHomepage?: boolean;
  enableZoocasaLightHomepage?: boolean;
  homepageInsights?: HomePageInsights | null;
}>;

export interface Maintenance {
  isMaintenance: boolean;
  maintenanceStartTime: string;
  maintenanceEndTime: string;
  maintenanceDate: string;
}

const HomePageLocalFocus = lazy(() => import('./themed-home-page-index/homepage-local-focus'));
const HomePageExpUS = lazy(() => import('./themed-home-page-index/homepage-exp-us'));
const HomePageExpInService = lazy(() => import('./themed-home-page-index/homepage-exp-in-service'));
const HomePageExpCommercial = dynamic(() => import('./themed-home-page-index/homepage-exp-commercial'), {
  loading: () => <LoadingText />,
});
const HomePageExpCommercialCA = dynamic(() => import('./themed-home-page-index/homepage-exp-commercial-ca'), {
  loading: () => <LoadingText />,
});

export default function HomePage(props: HomePageProps) {

  // manually set the maintenance props for the homepage and change when needed -- values are currently dummy values
  const maintenance = { isMaintenance: false, maintenanceStartTime: 'Start time', maintenanceEndTime: 'End time', maintenanceDate: 'Date' };
  const dynamicContentCardLinks = {
    [LOCALE_HOME_PATHS.CONTENT_CARDS_CARDS_SOLD_NEARBY_TITLE]: props.homePageUrls?.soldPricesUrl,
  };
  const tenant = getTenantById(props.tenantId);
  const homeConfig = getTenantHomeConfig(tenant);
  const contentCardsWithDynamicLink = homeConfig?.contentCards?.cards?.map(card => {
    if (card.hasDynamicLink) {
      return {
        ...card,
        link: dynamicContentCardLinks[card.title],
      };
    } 
    return card;
  });

  switch (props.tenantId) {
    case EXP_REALTY_CA_TENANT_ID:
      return <HomePageLocalFocus availableHomeListings={props.availableHomeListings} availableHomeListingsV2={props.availableHomeListingsV2} availableHomeListingsMeta={props.availableHomeListingsMeta} availableHomeListingsLocation={props.availableHomeListingsLocation} internalLinksData={props.internalLinksData} isExpandedArea={props.isExpandedArea} maintenance={maintenance} routeName={props.routeName} tenantId={props.tenantId} headerData={props.headerData} isCrawler={props.isCrawler} featuredHomesMlsNums={props.featuredHomesMlsNums} isNotificationBannerHidden={props.isNotificationBannerHidden} homePageUrls={props.homePageUrls} contentCardsWithDynamicLink={contentCardsWithDynamicLink} homepageInsights={props.homepageInsights} />;
    case EXP_REALTY_US_TENANT_ID:
      if (tenant.features.UseLocalFocusHomepage || props.enableLocalFocusHomepage) {
        return <HomePageLocalFocus availableHomeListings={props.availableHomeListings} availableHomeListingsV2={props.availableHomeListingsV2} availableHomeListingsMeta={props.availableHomeListingsMeta} availableHomeListingsLocation={props.availableHomeListingsLocation} internalLinksData={props.internalLinksData} isExpandedArea={props.isExpandedArea} maintenance={maintenance} routeName={props.routeName} tenantId={props.tenantId} headerData={props.headerData} isCrawler={props.isCrawler} featuredHomesMlsNums={props.featuredHomesMlsNums} isNotificationBannerHidden={props.isNotificationBannerHidden} homePageUrls={props.homePageUrls} contentCardsWithDynamicLink={contentCardsWithDynamicLink} homepageInsights={props.homepageInsights} />;
      }
      return <HomePageExpUS availableHomeListings={props.availableHomeListings} availableHomeListingsMeta={props.availableHomeListingsMeta} availableHomeListingsLocation={props.availableHomeListingsLocation} internalLinksData={props.internalLinksData} isExpandedArea={props.isExpandedArea} maintenance={maintenance} routeName={props.routeName} tenantId={props.tenantId} headerData={props.headerData} isCrawler={props.isCrawler} featuredHomesMlsNums={props.featuredHomesMlsNums} isNotificationBannerHidden={props.isNotificationBannerHidden} homePageUrls={props.homePageUrls} />;
    case EXP_IN_SERVICE_TENANT_ID:
      return <HomePageExpInService availableHomeListings={props.availableHomeListings} availableHomeListingsMeta={props.availableHomeListingsMeta} availableHomeListingsLocation={props.availableHomeListingsLocation} internalLinksData={props.internalLinksData} isExpandedArea={props.isExpandedArea} maintenance={maintenance} routeName={props.routeName} tenantId={props.tenantId} headerData={props.headerData} isCrawler={props.isCrawler} featuredHomesMlsNums={props.featuredHomesMlsNums} isNotificationBannerHidden={props.isNotificationBannerHidden} homePageUrls={props.homePageUrls} />;
    case EXP_COMMERCIAL_TENANT_ID:
      return <HomePageExpCommercial insightsCards={props.insightsCards} />;
    case EXP_COMMERCIAL_CA_TENANT_ID:
      return <HomePageExpCommercialCA insightsCards={props.insightsCards} />;
    case ZOOCASA_TENANT_ID:
      if (props.enableZoocasaLightHomepage) {
        return <HomePageZoocasaLight availableHomeListings={props.availableHomeListings} availableHomeListingsMeta={props.availableHomeListingsMeta} availableHomeListingsLocation={props.availableHomeListingsLocation} internalLinksData={props.internalLinksData} isExpandedArea={props.isExpandedArea} maintenance={maintenance} routeName={props.routeName} tenantId={props.tenantId} headerData={props.headerData} isCrawler={props.isCrawler} featuredHomesMlsNums={props.featuredHomesMlsNums} isNotificationBannerHidden={props.isNotificationBannerHidden} soldHomeListings={props.soldHomeListings} featuredHomeListings={props.featuredHomeListings} homePageUrls={props.homePageUrls} />;
      }
      return <HomePageZoocasa availableHomeListings={props.availableHomeListings} availableHomeListingsMeta={props.availableHomeListingsMeta} availableHomeListingsLocation={props.availableHomeListingsLocation} internalLinksData={props.internalLinksData} isExpandedArea={props.isExpandedArea} maintenance={maintenance} routeName={props.routeName} tenantId={props.tenantId} headerData={props.headerData} isCrawler={props.isCrawler} featuredHomesMlsNums={props.featuredHomesMlsNums} isNotificationBannerHidden={props.isNotificationBannerHidden} soldHomeListings={props.soldHomeListings} featuredHomeListings={props.featuredHomeListings} homePageUrls={props.homePageUrls} />;
    default:
      return <HomePageZoocasa availableHomeListings={props.availableHomeListings} availableHomeListingsMeta={props.availableHomeListingsMeta} availableHomeListingsLocation={props.availableHomeListingsLocation} internalLinksData={props.internalLinksData} isExpandedArea={props.isExpandedArea} maintenance={maintenance} routeName={props.routeName} tenantId={props.tenantId} headerData={props.headerData} isCrawler={props.isCrawler} featuredHomesMlsNums={props.featuredHomesMlsNums} isNotificationBannerHidden={props.isNotificationBannerHidden} soldHomeListings={props.soldHomeListings} featuredHomeListings={props.featuredHomeListings} homePageUrls={props.homePageUrls} />;
  }
}
